/**
 * tariff_v2 BOM tree helpers (pure functions).
 *
 * The play stores the unreviewed base tree plus a list of review corrections.
 * The tree the user approves is the base tree with the accepted corrections
 * replayed in order. replayCorrections mirrors the play's
 * pipeline/review.py apply_corrections exactly, so what the page previews is
 * what classify-assembly will use.
 */

const clone = (value) => JSON.parse(JSON.stringify(value));

export function parentMap(tree) {
    const parents = {};
    Object.keys(tree.nodes).forEach((id) => { parents[id] = null; });
    Object.entries(tree.nodes).forEach(([id, node]) => {
        node.children.forEach((child) => { parents[child] = id; });
    });
    return parents;
}

export function subtreeIds(tree, nodeId) {
    const out = [];
    const stack = [nodeId];
    while (stack.length) {
        const current = stack.pop();
        out.push(current);
        stack.push(...(tree.nodes[current]?.children || []));
    }
    return out;
}

function correctionProblem(c, tree, fileIds) {
    const node = tree.nodes[c.target_node_id];
    if (!node) return 'unknown target';
    const params = c.params || {};
    if (['exclude', 'remove_bom_item', 'reparent'].includes(c.action) && c.target_node_id === tree.root_id) {
        return 'root cannot change';
    }
    if (c.action === 'match' && (node.status !== 'missing' || !fileIds.has(params.file_id))) return 'match invalid';
    if (c.action === 'reparent' && (!tree.nodes[params.new_parent_node_id]
        || subtreeIds(tree, c.target_node_id).includes(params.new_parent_node_id))) return 'reparent invalid';
    if (c.action === 'adjust_qty' && !(params.qty > 0)) return 'qty invalid';
    return '';
}

function detach(tree, nodeId) {
    Object.values(tree.nodes).forEach((node) => {
        node.children = node.children.filter((id) => id !== nodeId);
    });
    tree.orphans = (tree.orphans || []).filter((id) => id !== nodeId);
}

export function replayCorrections(baseTree, corrections, acceptedIds) {
    const tree = clone(baseTree);
    (corrections || []).forEach((c) => {
        const fileIds = new Set(Object.values(tree.nodes).map((n) => n.file_id).filter(Boolean));
        if (!acceptedIds.has(c.correction_id) || correctionProblem(c, tree, fileIds)) return;
        const node = tree.nodes[c.target_node_id];
        const params = c.params || {};
        node.evidence = [...node.evidence, { kind: 'review_correction', detail: `${c.correction_id} ${c.action}` }];
        if (c.action === 'match') {
            Object.entries(tree.nodes)
                .filter(([id, n]) => n.file_id === params.file_id && ['unstructured', 'orphan'].includes(n.relationship) && id !== c.target_node_id)
                .forEach(([id, loose]) => {
                    node.children = [...node.children, ...loose.children];
                    detach(tree, id);
                    delete tree.nodes[id];
                });
            node.file_id = params.file_id;
            node.status = 'matched';
            node.candidates = [];
        } else if (c.action === 'exclude') {
            node.status = 'excluded';
        } else if (c.action === 'remove_bom_item') {
            node.status = 'invalid_extraction';
        } else if (c.action === 'reparent') {
            detach(tree, c.target_node_id);
            tree.nodes[params.new_parent_node_id].children.push(c.target_node_id);
            if (node.relationship === 'orphan') node.relationship = 'unstructured';
        } else if (c.action === 'adjust_qty') {
            node.qty = params.qty;
            node.qty_source = 'review';
        } else if (c.action === 'flag') {
            node.flags = { ...node.flags, review_flags: [...(node.flags.review_flags || []), params.note || c.reasoning] };
        }
    });
    return tree;
}

/** node_id -> effective quantity per assembly (null when the node or an ancestor is left out). */
export function effectiveQuantities(tree, decisions) {
    const out = {};
    const walk = (nodeId, multiplier, active) => {
        const node = tree.nodes[nodeId];
        const decision = decisions[nodeId] || {};
        const included = active && decision.included !== false && !['excluded', 'invalid_extraction'].includes(node.status);
        const qty = decision.qty != null && decision.qty !== '' ? Number(decision.qty) : node.qty;
        out[nodeId] = included ? multiplier * qty : null;
        node.children.forEach((child) => walk(child, included ? multiplier * qty : 0, included));
    };
    walk(tree.root_id, 1, true);
    return out;
}

/** The ApprovedTree payload (spec C6): every non-root node's decision plus every correction's decision. */
export function buildApprovedTree(tree, decisions, corrections, acceptedIds) {
    const nodes = Object.keys(tree.nodes)
        .filter((id) => id !== tree.root_id)
        .map((id) => {
            const d = decisions[id] || {};
            const entry = { node_id: id, included: d.included !== false };
            if (d.qty != null && d.qty !== '' && Number(d.qty) !== tree.nodes[id].qty) entry.qty = Number(d.qty);
            if (d.overrideCode && d.overrideCode.trim()) {
                entry.hts_override = { code: d.overrideCode.trim(), reason: (d.overrideReason || '').trim() || 'user override' };
            }
            if (d.unitCost != null && d.unitCost !== '') entry.unit_cost = { amount: Number(d.unitCost), currency: 'USD' };
            return entry;
        });
    return {
        root_id: tree.root_id,
        nodes,
        correction_decisions: (corrections || []).map((c) => ({ correction_id: c.correction_id, accepted: acceptedIds.has(c.correction_id) })),
    };
}

/** Decisions pre-filled from a previous approval, so a re-run keeps the user's edits. */
export function decisionsFromApproval(approval) {
    const out = {};
    (approval?.approved_tree?.nodes || []).forEach((n) => {
        out[n.node_id] = {
            included: n.included !== false,
            qty: n.qty ?? null,
            overrideCode: n.hts_override?.code || '',
            overrideReason: n.hts_override?.reason || '',
            unitCost: n.unit_cost?.amount ?? '',
        };
    });
    return out;
}
